<template>
  <div
    class="ag-dropzone"
    :data-over="isOver"
    @click="onClick"
    @dragenter.prevent="isOver = true"
    @dragleave.prevent="isOver = false"
    @dragover.prevent="isOver = true"
    @drop.prevent="onDrop"
  >
    <div v-if="!uploadFiles.length" class="ag-dropzone__message">
      <slot />
    </div>

    <div v-else class="ag-dropzone__container">
      <div v-for="file in uploadFiles" :key="file.id" class="ag-dropzone__file">
        <p>{{ file.size }}</p>
        <small>{{ file.name }}</small>
        <v-progress-linear
          bg-color="white"
          color="#cccccc"
          height="16px"
          style="border-radius: 8px; max-width: 80px; min-height: 16px; align-self: center"
          :model-value="file.progress"
        />
        <AGButton type="secondary" :data-hover="true" @click.prevent.stop="onAbort(file)">
          {{ labelCancelUpload }}
        </AGButton>
      </div>
    </div>

    <input ref="refFileInput" type="file" :accept="accept" :multiple="multiple" @change="onHandleFiles" />
  </div>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import UtilsString from '@/utils/UtilsString';

class AGFile {
  /**
   * @param {File} file
   * @param {XMLHttpRequest} xhr
   */
  constructor(file, xhr) {
    this.id = UtilsString.generateUuid();
    this.size = UtilsString.formatBytes(file.size);
    this.name = file.name;
    this.progress = 0;

    xhr.upload.addEventListener('progress', (e) => {
      this.progress = (e.loaded * 100.0) / e.total || 100;
    });

    this.xhr = xhr;
  }
}

export default {
  components: { AGButton },

  emits: ['complete', 'error', 'limitReached', 'fileSizeExceeded'],
  props: {
    multiple: {
      type: Boolean,
      default: false,
    },

    accept: {
      type: String,
      default: undefined,
    },

    maxFiles: {
      type: Number,
      default: undefined,
    },

    maxFileSize: {
      type: Number,
      default: undefined,
    },

    url: {
      type: String,
      required: true,
    },

    labelCancelUpload: {
      type: String,
      default: 'Cancelar upload',
    },

    customUpload: {
      type: Function,
      default: undefined,
    },
  },

  data() {
    return {
      isOver: false,
      uploadFiles: [],
    };
  },

  methods: {
    onClick() {
      this.$refs.refFileInput.click();
    },

    onDrop(e) {
      this.isOver = false;
      this.onHandleFiles(e.dataTransfer);
    },

    onHandleFiles(event) {
      const files = event.files ? [...event.files] : [...event.target.files];

      const filesProcessing = this.uploadFiles.length + files.length;

      if (filesProcessing > this.maxFiles) return this.$emit('limitReached');

      files.forEach((file) => {
        if (file.size > this.maxFileSize) return this.$emit('fileSizeExceeded');

        if (this.customUpload) {
          return this.customUpload(file);
        }

        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        const agFile = new AGFile(file, xhr);
        this.uploadFiles.push(agFile);

        xhr.addEventListener('readystatechange', () => {
          if (xhr.readyState !== 4) return;

          this.removeFile(agFile);

          if (xhr.status === 200) {
            this.$emit('complete');
          } else if (xhr.status !== 200) {
            this.$emit('error', xhr.statusText);
          }
        });

        xhr.open('POST', this.url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('access_Token')}`);
        xhr.setRequestHeader('ipAddress', localStorage.getItem('ipAddress'));
        formData.append('file', file);
        xhr.send(formData);
      });
    },

    onAbort(agFile) {
      if (window.confirm('Você tem certeza que deseja cancelar o upload?')) {
        agFile.xhr.abort();
        this.removeFile(agFile);
      }
    },

    removeFile(agFile) {
      const index = this.uploadFiles.findIndex((file) => file.id === agFile.id);

      if (index === -1) return;

      this.uploadFiles.splice(index, 1);
    },
  },
};
</script>

<style scoped lang="scss">
.ag-dropzone {
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 2px solid #e5e5e5;
  font-family: Arial, sans-serif;
  color: #777;
  transition: 0.2s linear;
  min-height: 150px;
  cursor: pointer;

  .ag-dropzone__message {
    margin: 2em 0;
    text-align: center;
  }

  .ag-dropzone__container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
  }

  .ag-dropzone__file {
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: #49a7f1;
    height: 200px;
    width: 200px;
    padding: 2em 1em;
    color: #fff;

    p {
      margin: 0;
    }

    &:hover {
      .ag-btn-secondary {
        visibility: visible;
      }
    }
  }

  .ag-btn-secondary {
    visibility: hidden;
  }

  &:hover {
    background-color: #f6f6f6;
    opacity: 0.8;
  }

  &[data-over='true'] .ag-dropzone__message {
    opacity: 0.5;
  }

  input[type='file'] {
    display: none;
  }
}
</style>
